import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  Skeleton,
  Alert,
  Button,
  Paper,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { DownloadOutlined as Download, ShareOutlined as Share, CalendarTodayOutlined as CalendarToday, DeleteOutlined as Delete } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { fetchSummary, clearCurrentSummary, deleteSummary } from '../store/slices/summariesSlice';
import EnhancedMarkdownRenderer from '../components/EnhancedMarkdownRenderer';
import { format } from 'date-fns';

const SummaryView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const currentSummary = useAppSelector((state: any) => state.summaries.currentSummary);
  const loading = useAppSelector((state: any) => state.summaries.loading);
  const error = useAppSelector((state: any) => state.summaries.error);

  useEffect(() => {
    if (id) {
      dispatch(fetchSummary(id));
    }

    return () => {
      dispatch(clearCurrentSummary());
    };
  }, [dispatch, id]);

  const formatSafeDate = (dateValue: any, formatString: string) => {
    if (!dateValue) return 'N/A';
    try {
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) return 'N/A';
      return format(date, formatString);
    } catch {
      return 'N/A';
    }
  };

  const handleDownload = () => {
    if (!currentSummary) return;

    const createdAt = formatSafeDate(currentSummary.created_at, 'MMM dd, yyyy HH:mm');

    const content = `# ${currentSummary.title}

**Generated:** ${createdAt}
${currentSummary.category ? `**Category:** ${currentSummary.category}` : ''}
${currentSummary.process_id ? `**Process ID:** ${currentSummary.process_id}` : ''}

${currentSummary.content}
    `;

    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${currentSummary.title.replace(/\s+/g, '_')}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleShare = () => {
    if (navigator.share && currentSummary) {
      navigator.share({
        title: currentSummary.title,
        text: `Check out this email summary: ${currentSummary.title}`,
        url: window.location.href,
      });
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      // You could add a toast notification here
    }
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (id) {
      await dispatch(deleteSummary(id));
      setDeleteDialogOpen(false);
      navigate('/summaries');
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ mb: 3 }}>
          <Skeleton variant="text" width={300} height={40} />
        </Box>
        <Card>
          <CardContent>
            <Skeleton variant="text" width="100%" height={60} />
            <Skeleton variant="text" width="80%" height={20} />
            <Skeleton variant="text" width="60%" height={20} />
            <Box sx={{ mt: 3 }}>
              <Skeleton variant="rectangular" width="100%" height={400} />
            </Box>
          </CardContent>
        </Card>
      </Container>
    );
  }

  if (error || !currentSummary) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error || 'Summary not found'}
        </Alert>
        <Button
          variant="outlined"
          onClick={() => navigate('/summaries')}
          >
            Back to Summaries
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Button
            variant="outlined"
            onClick={() => navigate('/summaries')}
            sx={{ mb: 2 }}
          >
            Back to Summaries
          </Button>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 500 }}>
            {currentSummary.title}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={handleDownload}
          >
            Download
          </Button>
          <Button
            variant="outlined"
            startIcon={<Share />}
            onClick={handleShare}
          >
            Share
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<Delete />}
            onClick={handleDeleteClick}
          >
            Delete
          </Button>
        </Box>
      </Box>

      {/* Summary Info */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
            <Chip
              label={currentSummary.status}
              color={getStatusColor(currentSummary.status) as any}
              size="small"
            />
            {currentSummary.category && (
              <Chip
                label={currentSummary.category}
                variant="outlined"
                size="small"
                color="primary"
              />
            )}
            {currentSummary.date_range_display && (
              <Chip
                icon={<CalendarToday />}
                label={currentSummary.date_range_display}
                variant="outlined"
                size="small"
                color="secondary"
              />
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarToday sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                Summary generated from data analysis
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Generated on {formatSafeDate(currentSummary.created_at, 'MMM dd, yyyy HH:mm')}
            </Typography>
            {currentSummary.process_id && (
              <Typography variant="body2" color="text.secondary">
                Process ID: {currentSummary.process_id}
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Summary Content */}
      <Paper sx={{ p: 4 }}>
        <EnhancedMarkdownRenderer
          content={currentSummary.content}
          variant="full"
          skipFirstH1={currentSummary.title}
        />
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Delete Summary
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this summary? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SummaryView;
